import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Workspace, GeneratedPost, SocialNetwork, PlatformPostCounts, CONTENT_CATEGORIES, ContentCategory, TextElement, isPastelUri, parsePastelUri, ImageSource, AIModel, PREVIEW_FORMATS, PreviewFormat, ReviewCapability } from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { reviewApi } from '../services/reviewService';
import { useAuth } from '../hooks/useAuth';
import { useImageSourceConfig, useTemplateAndThumbnails } from '../hooks/generatePage';
import { Button } from '../components/Button';
import { Card, CardContent } from '../components/Card';
import { NovahGenerateChat, UserPlan } from '../components/NovahGenerateChat';
import { InputWrapper, Label, TextArea, ErrorMessage } from '../components/Input';
import { FormatSelector } from '../components/FormatSelector';
import { Toast } from '../components/Toast';
import { PreviewModal } from '../components/PreviewModal';
import { PageSkeleton } from '../components/PageSkeleton';
import { renderCompositeThumbnail } from '../utils/canvasRenderer';
import { NETWORK_ICONS } from '../utils/networkIcons';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }
`;

const SocialNetworkGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const PlatformRow = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: 8px;
  background-color: ${({ theme, $active }) => ($active ? theme.colors.warning : 'transparent')};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
`;

const PlatformLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
  cursor: pointer;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const PlatformName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const CountInput = styled.input`
  width: 60px;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    cursor: not-allowed;
  }
`;

// Image Source Selector Styles
const ImageSourceSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ImageSourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ImageSourceOption = styled.button<{ $active: boolean; $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: 8px;
  background-color: ${({ theme, $active }) => ($active ? theme.colors.warning : 'transparent')};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-family: inherit;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ImageSourceIcon = styled.span`
  font-size: 24px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const ImageSourceLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  text-align: center;
`;

const ImageSourceDesc = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin-top: 2px;
`;

const ImageMixSlider = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const SliderLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 80px;
`;

const SliderValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  min-width: 35px;
  text-align: right;
`;

const RangeSlider = styled.input`
  flex: 1;
  height: 6px;
  -webkit-appearance: none;
  background: ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 8px;
    cursor: pointer;
  }
`;

const AssetSelectorContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
`;

const AssetSelectorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const AssetSelectorTitle = styled.span`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const AssetSelectorButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const SmallButton = styled.button`
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.border};
  }
`;

const AssetSelector = styled.div`
  max-height: 150px;
  overflow-y: auto;
`;

const AssetItem = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  background-color: ${({ theme, $selected }) => ($selected ? theme.colors.warning : 'transparent')};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }

  input {
    display: none;
  }
`;

const AssetThumb = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.colors.border};
`;

const AssetName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Custom Color Picker Styles
const ColorPickerContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
`;

const ColorPickerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ColorPickerTitle = styled.span`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ColorInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
`;

const ColorInput = styled.input`
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  cursor: pointer;
  background: transparent;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  &::-webkit-color-swatch {
    border: none;
    border-radius: 8px;
  }
`;

const AddColorButton = styled.button`
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

const SelectedColorsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm};
  min-height: 50px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ColorChip = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const ColorSwatch = styled.span<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 8px;
  background-color: ${({ $color }) => $color};
  border: 2px solid ${({ theme }) => theme.colors.border};
`;

const ColorNumber = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 16px;
`;

const RemoveColorButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 2px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: 8px;
  cursor: pointer;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.error};
    color: white;
  }
`;

const NoColorsMessage = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
`;

const CategoryBadge = styled.span<{ $category: ContentCategory }>`
  display: inline-block;
  padding: 1px 5px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ $category }) => {
    switch ($category) {
      case 'education': return '#93C5FD';
      case 'connection': return '#D8B4FE';
      case 'proof': return '#86EFAC';
      case 'promotion': return '#FDE68A';
      default: return '#f5f5f5';
    }
  }};
  color: ${({ theme }) => theme.colors.text};
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

const PostCard = styled(Card)`
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover { transform: translateY(-2px); box-shadow: ${({ theme }) => theme.shadows.md}; }
  position: relative;
`;

const PostImage = styled.div<{ $aspectRatio?: number }>`
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || (9 / 16)};
  overflow: hidden;
  position: relative;
  background: ${({ theme }) => theme.colors.surfaceHover};
  img { width: 100%; height: 100%; object-fit: contain; }
`;

const HoverOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  background: linear-gradient(transparent, rgba(0,0,0,0.8));
  color: white;
  opacity: 0;
  transition: opacity 0.2s ease;
  ${PostCard}:hover & { opacity: 1; }
`;

const PostCardFooter = styled.div`
  padding: 4px 8px;
  display: flex;
  gap: 3px;
  align-items: center;
  flex-wrap: wrap;
`;

const SelectCheckbox = styled.div<{ $selected: boolean }>`
  position: absolute;
  top: 6px;
  left: 6px;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 2px solid ${({ $selected }) => $selected ? '#3B82F6' : 'rgba(255,255,255,0.7)'};
  background: ${({ $selected }) => $selected ? '#3B82F6' : 'rgba(0,0,0,0.3)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  cursor: pointer;
  z-index: 5;
  transition: all 0.15s;
  opacity: ${({ $selected }) => $selected ? 1 : 0};
  ${PostCard}:hover & { opacity: 1; }
`;

const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: 10px 16px;
  background: #3B82F6;
  color: white;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 600;
  flex-wrap: wrap;
`;

const SelectionBarBtn = styled.button`
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.5);
  background: transparent;
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.2); }
`;

const SelectionTemplateSelect = styled.select`
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.5);
  background: rgba(255,255,255,0.15);
  color: white;
  font-size: 12px;
  cursor: pointer;
  option { color: #333; background: white; }
`;

const ImageSourceBadge = styled.span<{ $source: ImageSource }>`
  display: inline-block;
  padding: 1px 5px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ $source }) => {
    switch ($source) {
      case 'stock': return '#93C5FD';
      case 'pastel': return '#D8B4FE';
      case 'asset': return '#86EFAC';
      case 'ai': return '#FDE68A';
      default: return '#f3f4f6';
    }
  }};
  color: ${({ theme }) => theme.colors.text};
`;

const IMAGE_SOURCE_LABELS: Record<ImageSource, string> = {
  stock: 'Stock',
  pastel: 'Color',
  asset: 'Custom',
  ai: 'AI',
};

// AI model display labels
const AI_MODEL_LABELS: Record<NonNullable<AIModel>, string> = {
  'kie': 'KIE',
  'cloudflare-sd': 'SD',
};

// Get display label for image source, including AI model name if applicable
const getImageSourceLabel = (imageSource: ImageSource, aiModel?: AIModel | null, aiModelDetail?: string | null): string => {
  if (imageSource === 'ai') {
    if (aiModelDetail) return aiModelDetail;
    if (aiModel) return AI_MODEL_LABELS[aiModel] || 'AI';
    return 'AI';
  }
  return IMAGE_SOURCE_LABELS[imageSource];
};

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const SkeletonImage = styled.div<{ $aspectRatio?: number }>`
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || (9 / 16)};
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.surfaceHover} 25%, ${({ theme }) => theme.colors.background} 50%, ${({ theme }) => theme.colors.surfaceHover} 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const SkeletonLine = styled.div<{ $width?: string }>`
  height: 12px;
  width: ${({ $width }) => $width || '100%'};
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.surfaceHover} 25%, ${({ theme }) => theme.colors.background} 50%, ${({ theme }) => theme.colors.surfaceHover} 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

const SkeletonContent = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StreamProgressBarContainer = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  height: 6px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  overflow: hidden;
`;

const StreamProgressBarFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: width 0.3s ease;
`;

const StreamProgressText = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const PastelBackground = styled.div<{ $color: string }>`
  width: 100%;
  height: 100%;
  background-color: ${({ $color }) => $color};
`;

const AiLoadingOverlay = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.warning};
  gap: ${({ theme }) => theme.spacing.sm};
`;

const AiLoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AiLoadingText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const TemplateSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PostNetwork = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  color: white;
  background: ${({ theme }) => theme.colors.primary};
  padding: 1px 6px;
  border-radius: 6px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const GeneratingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.md};
  text-align: center;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const GeneratingText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const BulkActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const BulkLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const ProgressBar = styled.div`
  flex: 1;
  min-width: 200px;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid ${({ theme }) => theme.colors.border};
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background-color: ${({ theme }) => theme.colors.primary};
  transition: width 0.3s ease;
`;

const ProgressText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 80px;
`;

const RegenModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const RegenModalContent = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  min-width: 300px;
  max-width: 400px;
`;

const RegenModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

const RegenOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const RegenOption = styled.button<{ $loading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: ${({ $loading }) => ($loading ? 'wait' : 'pointer')};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  opacity: ${({ $loading }) => ($loading ? 0.7 : 1)};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.border};
    color: white;
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const RegenCancelButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const SOCIAL_NETWORKS: { id: SocialNetwork; label: string }[] = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'twitter', label: 'Twitter/X' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'threads', label: 'Threads' },
  { id: 'gbp', label: 'Google Business' },
  { id: 'bluesky', label: 'Bluesky' },
];


// Response type for generate API
interface GenerateResponse {
  posts: GeneratedPost[];
  creditsUsed: number;
  creditsRemaining: number;
}

export function GeneratePage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, updateCredits } = useAuth();
  const [quickGenPending, setQuickGenPending] = useState(false);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [userPlan, setUserPlan] = useState<UserPlan>('free');
  const [posts, setPosts] = useState<GeneratedPost[]>([]);
  const [platformCounts, setPlatformCounts] = useState<PlatformPostCounts>({
    instagram: 2,
    facebook: 2,
  });
  const [customPrompt, setCustomPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAiFallbackToast, setShowAiFallbackToast] = useState(false);
  const [error, setError] = useState('');
  const [selectedPost, setSelectedPost] = useState<GeneratedPost | null>(null);
  const [selectedPostIds, setSelectedPostIds] = useState<Set<string>>(new Set());
  const [regenPostId, setRegenPostId] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState<'image' | 'text' | 'all' | null>(null);
  const [previewFormat, setPreviewFormat] = useState<PreviewFormat>('vertical');
  const [reviewCapability, setReviewCapability] = useState<ReviewCapability | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingPosts, setStreamingPosts] = useState<GeneratedPost[]>([]);
  const [skeletonCount, setSkeletonCount] = useState(0);
  const [streamProgress, setStreamProgress] = useState({ current: 0, total: 0 });

  // Extracted hooks
  const imageSourceConfig = useImageSourceConfig(workspaceId);
  const {
    enabledImageSources, imageMix, backgroundImages, selectedAssetIds,
    customColors, colorPickerValue, setColorPickerValue,
    fetchBackgroundImages, toggleImageSource, updateImageMix,
    toggleAssetSelection, selectAllAssets, deselectAllAssets,
    addCustomColor, removeCustomColor, clearAllColors,
  } = imageSourceConfig;

  const templateState = useTemplateAndThumbnails(workspaceId, posts, workspace, setPosts);
  const {
    templates, bulkTemplateId, setBulkTemplateId, bulkTemplate,
    solidTemplate, postTemplateOverrides,
    postFormatOverrides,
    compositeThumbnails, setCompositeThumbnails,
    workspaceLogoUrl, buildBrandRenderOptions,
    isBulkProcessing, bulkProgress,
    templateError,
    fetchTemplates, fetchWorkspaceLogo, fetchBrandInfo,
    getTemplateForPost,
    handlePostTemplateChange, handlePreviewSaveOffset,
    handleTemplateCreated, handleBulkApplyAndDownload,
  } = templateState;

  // Check for quick-gen query param
  useEffect(() => {
    if (searchParams.get('quickgen') === 'true') {
      setQuickGenPending(true);
      setSearchParams({}, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (workspaceId) {
      fetchWorkspace();
      fetchPosts();
      fetchTemplates();
      fetchBackgroundImages();
      fetchWorkspaceLogo();
      fetchBrandInfo();
    }
    // Fetch user plan
    apiClient.get<{ planId: string }>('/api/billing/subscription')
      .then(sub => setUserPlan((sub.planId || 'free') as UserPlan))
      .catch(() => setUserPlan('free'));
    reviewApi.getReviewCapability()
      .then(setReviewCapability)
      .catch(() => setReviewCapability(null));
  }, [workspaceId]);

  const fetchWorkspace = async () => {
    try {
      const data = await apiClient.get<Workspace>(`/api/workspaces/${workspaceId}`);
      setWorkspace(data);
    } catch (error) {
      console.error('Failed to fetch workspace:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const data = await apiClient.get<GeneratedPost[]>(`/api/workspaces/${workspaceId}/posts`);
      setPosts(data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  };

  // Note: fetchTemplates, fetchBackgroundImages, fetchWorkspaceLogo provided by extracted hooks

  // Poll for pending AI images
  useEffect(() => {
    // Find posts with pending AI images (have taskId but no imageUrl)
    const pendingPosts = posts.filter(
      (p) => p.pendingImageTaskId && !p.imageUrl
    );

    if (pendingPosts.length === 0 || !workspaceId) return;

    const taskIds = pendingPosts.map((p) => p.pendingImageTaskId!);
    let isCancelled = false;

    const pollForImages = async () => {
      try {
        const response = await apiClient.get<{
          tasks: Array<{
            taskId: string;
            state: 'waiting' | 'success' | 'fail';
            imageUrl?: string;
            error?: string;
          }>;
        }>(`/api/workspaces/${workspaceId}/ai-image-status?taskIds=${taskIds.join(',')}`);

        if (isCancelled) return;

        // Update posts with completed images
        const completedTasks = response.tasks.filter((t) => t.state === 'success' && t.imageUrl);
        const failedTasks = response.tasks.filter((t) => t.state === 'fail');

        if (completedTasks.length > 0 || failedTasks.length > 0) {
          setPosts((prevPosts) =>
            prevPosts.map((post) => {
              const completed = completedTasks.find((t) => t.taskId === post.pendingImageTaskId);
              if (completed) {
                return {
                  ...post,
                  imageUrl: completed.imageUrl!,
                  pendingImageTaskId: null,
                };
              }
              const failed = failedTasks.find((t) => t.taskId === post.pendingImageTaskId);
              if (failed) {
                // Mark as failed - clear the pending task ID
                return {
                  ...post,
                  pendingImageTaskId: null,
                };
              }
              return post;
            })
          );
        }
      } catch (err) {
        console.error('Failed to poll AI image status:', err);
      }
    };

    // Poll immediately and then every 3 seconds
    pollForImages();
    const interval = setInterval(pollForImages, 3000);

    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [posts, workspaceId]);

  // Template/thumbnail logic provided by useTemplateAndThumbnails hook

  const togglePlatform = useCallback((platform: SocialNetwork) => {
    setPlatformCounts((prev) => {
      if (prev[platform] !== undefined) {
        // Remove platform
        const newCounts = { ...prev };
        delete newCounts[platform];
        return newCounts;
      } else {
        // Add platform with default count of 2
        return { ...prev, [platform]: 2 };
      }
    });
  }, []);

  const updatePlatformCount = useCallback((platform: SocialNetwork, count: number) => {
    setPlatformCounts((prev) => ({
      ...prev,
      [platform]: Math.max(0, Math.min(20, count)),
    }));
  }, []);

  // Image source callbacks provided by useImageSourceConfig hook

  // Memoized total post count
  const totalPostCount = useMemo(() => {
    return Object.values(platformCounts).reduce((sum, count) => sum + (count || 0), 0);
  }, [platformCounts]);

  // Memoized posts with images
  const postsWithImages = useMemo(() => {
    return posts.filter((p) => p.imageUrl);
  }, [posts]);

  const handleGenerate = useCallback(async () => {
    if (totalPostCount === 0) return;

    // Check if user has enough credits
    if (!user || user.credits < totalPostCount) {
      setError(`Not enough credits. You need ${totalPostCount} credits but only have ${user?.credits ?? 0}.`);
      return;
    }

    // Validate asset source has selected assets
    if (enabledImageSources.includes('asset') && selectedAssetIds.length === 0) {
      setError('Please select at least one asset image to use');
      return;
    }

    setIsGenerating(true);
    setError('');
    try {
      const response = await apiClient.post<GenerateResponse>(`/api/workspaces/${workspaceId}/generate`, {
        platformCounts,
        customPrompt,
        imageSourceConfig: {
          enabledSources: enabledImageSources,
          mix: imageMix,
          selectedAssetIds: enabledImageSources.includes('asset') ? selectedAssetIds : [],
          customColors: enabledImageSources.includes('pastel') ? customColors : [],
        },
      });
      setPosts([...response.posts, ...posts]);
      // Update credits in auth context
      updateCredits(response.creditsRemaining);
      // Check if any posts had AI fallback
      const hadAiFallback = response.posts.some(post => post.aiFallback);
      if (hadAiFallback) {
        setShowAiFallbackToast(true);
      }
    } catch (err) {
      console.error('Failed to generate posts:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate posts');
    } finally {
      setIsGenerating(false);
    }
  }, [workspaceId, platformCounts, customPrompt, totalPostCount, posts, enabledImageSources, imageMix, selectedAssetIds, user, updateCredits]);

  // Keep handleGenerate as non-streaming fallback
  void handleGenerate;

  const handleGenerateStream = useCallback(async () => {
    if (totalPostCount === 0) return;

    // Check if user has enough credits
    if (!user || user.credits < totalPostCount) {
      setError(`Not enough credits. You need ${totalPostCount} credits but only have ${user?.credits ?? 0}.`);
      return;
    }

    // Validate asset source has selected assets
    if (enabledImageSources.includes('asset') && selectedAssetIds.length === 0) {
      setError('Please select at least one asset image to use');
      return;
    }

    setIsStreaming(true);
    setSkeletonCount(totalPostCount);
    setStreamingPosts([]);
    setStreamProgress({ current: 0, total: totalPostCount });
    setError('');

    try {
      await apiClient.stream(
        `/api/workspaces/${workspaceId}/generate-stream`,
        {
          platformCounts,
          customPrompt,
          imageSourceConfig: {
            enabledSources: enabledImageSources,
            mix: imageMix,
            selectedAssetIds: enabledImageSources.includes('asset') ? selectedAssetIds : [],
            customColors: enabledImageSources.includes('pastel') ? customColors : [],
          },
        },
        (event, data) => {
          switch (event) {
            case 'init':
              setStreamProgress(prev => ({ ...prev, total: (data as { totalPosts: number }).totalPosts }));
              setSkeletonCount((data as { totalPosts: number }).totalPosts);
              break;
            case 'post': {
              const postData = data as { post: GeneratedPost; index: number; total: number };
              const newPost = postData.post;
              setStreamingPosts(prev => [...prev, newPost]);
              setStreamProgress(prev => ({ ...prev, current: postData.index + 1 }));
              setSkeletonCount(prev => Math.max(0, prev - 1));

              // Generate thumbnail immediately for the arriving post
              if (newPost.imageUrl) {
                const templateForPost = bulkTemplate
                  ? getTemplateForPost(newPost, bulkTemplate)
                  : null;
                if (templateForPost) {
                  // Build dynamic texts inline (can't use buildDynamicTextsMap since post isn't in posts[] yet)
                  const dynamicTexts: Record<string, string> = {};
                  if (newPost.dynamicTexts) {
                    for (const element of templateForPost.elements) {
                      if (element.elementType === 'text') {
                        const textEl = element as TextElement;
                        if (textEl.isDynamic && textEl.dynamicType) {
                          const dt = newPost.dynamicTexts as unknown as Record<string, string>;
                          const val = dt[textEl.dynamicType];
                          if (val) dynamicTexts[element.id] = val;
                        }
                      }
                    }
                  }
                  renderCompositeThumbnail(
                    newPost.imageUrl,
                    templateForPost,
                    400,
                    { dynamicTexts, workspaceLogoUrl, logoScale: workspace?.logoScale, ...buildBrandRenderOptions(templateForPost, newPost) }
                  ).then(thumbnailUrl => {
                    if (thumbnailUrl) {
                      setCompositeThumbnails(prev => ({ ...prev, [newPost.id]: thumbnailUrl }));
                    }
                  });
                }
              }
              break;
            }
            case 'complete': {
              const completeData = data as { creditsUsed: number; creditsRemaining: number };
              updateCredits(completeData.creditsRemaining);
              setIsStreaming(false);
              setSkeletonCount(0);
              // Move streaming posts into main posts state
              setStreamingPosts(prev => {
                setPosts(existingPosts => [...prev, ...existingPosts]);
                return [];
              });
              break;
            }
            case 'error':
              console.error('Stream error:', data);
              setError((data as { message?: string }).message || 'Generation error occurred');
              break;
          }
        }
      );
    } catch (err) {
      console.error('Stream failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate posts');
      // Move any already-received streaming posts into main posts state
      setStreamingPosts(prev => {
        if (prev.length > 0) {
          setPosts(existingPosts => [...prev, ...existingPosts]);
        }
        return [];
      });
    } finally {
      setIsStreaming(false);
      setSkeletonCount(0);
    }
  }, [workspaceId, platformCounts, customPrompt, totalPostCount, enabledImageSources, imageMix, selectedAssetIds, customColors, user, updateCredits, bulkTemplate, solidTemplate, workspaceLogoUrl, buildBrandRenderOptions]);

  // Quick Generate: auto-trigger generation once workspace data is loaded
  useEffect(() => {
    if (quickGenPending && workspace && !isStreaming && !isGenerating && totalPostCount > 0 && user && user.credits >= totalPostCount) {
      setQuickGenPending(false);
      handleGenerateStream();
    }
  }, [quickGenPending, workspace, isStreaming, isGenerating, totalPostCount, user]);



  const handleDeletePost = useCallback(async (postId: string) => {
    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}/posts/${postId}`);
      setPosts((prevPosts) => prevPosts.filter((p) => p.id !== postId));
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  }, [workspaceId]);
  // @ts-expect-error handleDeletePost will be wired to PreviewModal in a future iteration
  globalThis.__handleDeletePost = handleDeletePost;

  const handleRegenerate = useCallback(async (type: 'image' | 'text' | 'all') => {
    if (!regenPostId) return;

    setIsRegenerating(type);
    try {
      const updatedPost = await apiClient.post<GeneratedPost>(
        `/api/workspaces/${workspaceId}/posts/${regenPostId}/regenerate`,
        { type }
      );
      setPosts((prevPosts) => prevPosts.map((p) => (p.id === regenPostId ? updatedPost : p)));
      setRegenPostId(null);
    } catch (error) {
      console.error('Failed to regenerate:', error);
      setError('Failed to regenerate post');
    } finally {
      setIsRegenerating(null);
    }
  }, [regenPostId, workspaceId]);

  const handleNovahGenerate = useCallback(async (config: {
    platformCounts: PlatformPostCounts;
    customPrompt: string;
    imageStyle: 'stock' | 'pastel' | 'asset' | 'ai' | 'mix';
    contentType?: 'education' | 'connection' | 'proof' | 'promotion' | 'mix';
    kieModel?: string;
  }) => {
    const total = Object.values(config.platformCounts).reduce((s, c) => s + (c || 0), 0);
    if (total === 0 || !user || user.credits < total) return;

    // Map style to image source config — mix is plan-aware
    const styleMap: Record<string, { sources: ImageSource[]; mix: Record<string, number> }> = {
      stock: { sources: ['stock'], mix: { stock: 100, pastel: 0, asset: 0, ai: 0 } },
      pastel: { sources: ['pastel'], mix: { stock: 0, pastel: 100, asset: 0, ai: 0 } },
      asset: { sources: ['asset'], mix: { stock: 0, pastel: 0, asset: 100, ai: 0 } },
      ai: { sources: ['ai'], mix: { stock: 0, pastel: 0, asset: 0, ai: 100 } },
      mix: userPlan === 'business'
        ? backgroundImages.length > 0
          ? { sources: ['stock', 'asset', 'pastel', 'ai'], mix: { stock: 40, pastel: 10, asset: 40, ai: 10 } }
          : { sources: ['stock', 'pastel', 'ai'], mix: { stock: 80, pastel: 10, asset: 0, ai: 10 } }
        : { sources: ['stock', 'pastel'], mix: { stock: 60, pastel: 40, asset: 0, ai: 0 } },
    };
    const imgConfig = styleMap[config.imageStyle] || styleMap.mix;

    setPlatformCounts(config.platformCounts);
    setCustomPrompt(config.customPrompt);
    setIsStreaming(true);
    setSkeletonCount(total);
    setStreamingPosts([]);
    setStreamProgress({ current: 0, total });
    setError('');

    try {
      await apiClient.stream(
        `/api/workspaces/${workspaceId}/generate-stream`,
        {
          platformCounts: config.platformCounts,
          customPrompt: config.customPrompt,
          contentType: config.contentType,
          imageSourceConfig: {
            enabledSources: imgConfig.sources,
            mix: imgConfig.mix,
            selectedAssetIds: imgConfig.sources.includes('asset')
              ? (selectedAssetIds.length > 0 ? selectedAssetIds : backgroundImages.map(img => img.id))
              : [],
            customColors: config.imageStyle === 'pastel' ? customColors : [],
            kieModel: config.kieModel,
          },
        },
        (event, data) => {
          switch (event) {
            case 'init':
              setStreamProgress(prev => ({ ...prev, total: (data as { totalPosts: number }).totalPosts }));
              setSkeletonCount((data as { totalPosts: number }).totalPosts);
              break;
            case 'post': {
              const postData = data as { post: GeneratedPost; index: number; total: number };
              setStreamingPosts(prev => [...prev, postData.post]);
              setStreamProgress(prev => ({ ...prev, current: postData.index + 1 }));
              setSkeletonCount(prev => Math.max(0, prev - 1));

              if (postData.post.imageUrl && bulkTemplate) {
                const templateForPost = getTemplateForPost(postData.post, bulkTemplate);
                if (templateForPost) {
                  const dynamicTexts: Record<string, string> = {};
                  if (postData.post.dynamicTexts) {
                    for (const element of templateForPost.elements) {
                      if (element.elementType === 'text') {
                        const textEl = element as TextElement;
                        if (textEl.isDynamic && textEl.dynamicType) {
                          const dt = postData.post.dynamicTexts as unknown as Record<string, string>;
                          if (dt[textEl.dynamicType]) dynamicTexts[element.id] = dt[textEl.dynamicType];
                        }
                      }
                    }
                  }
                  renderCompositeThumbnail(postData.post.imageUrl, templateForPost, 400, { dynamicTexts, workspaceLogoUrl, logoScale: workspace?.logoScale, ...buildBrandRenderOptions(templateForPost, postData.post) })
                    .then(url => { if (url) setCompositeThumbnails(prev => ({ ...prev, [postData.post.id]: url })); });
                }
              }
              break;
            }
            case 'complete': {
              const d = data as { creditsUsed: number; creditsRemaining: number };
              updateCredits(d.creditsRemaining);
              setIsStreaming(false);
              setSkeletonCount(0);
              setStreamingPosts(prev => { setPosts(existing => [...prev, ...existing]); return []; });
              break;
            }
            case 'error':
              setError((data as { message?: string }).message || 'Generation error');
              break;
          }
        }
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate posts');
      setStreamingPosts(prev => { if (prev.length > 0) setPosts(existing => [...prev, ...existing]); return []; });
    } finally {
      setIsStreaming(false);
      setSkeletonCount(0);
    }
  }, [workspaceId, user, updateCredits, selectedAssetIds, backgroundImages, customColors, bulkTemplate, getTemplateForPost, workspaceLogoUrl, workspace?.logoScale, buildBrandRenderOptions, userPlan]);

  if (!workspace) {
    return <PageSkeleton />;
  }

  return (
    <>
      <PageHeader>
        <div>
          <Title>Generate Content</Title>
          <Subtitle>Create AI-powered posts for {workspace.name}</Subtitle>
        </div>
        <Button $variant="outline" onClick={() => navigate(`/workspace/${workspaceId}`)}>
          Back to Workspace
        </Button>
      </PageHeader>

      <Grid>
        <div>
          <Card>
            <CardContent>
              <NovahGenerateChat
                workspaceName={workspace.name}
                credits={user?.credits ?? 0}
                hasBackgroundImages={backgroundImages.length > 0}
                userPlan={userPlan}
                onNavigateBilling={() => navigate('/billing')}
                onGenerate={handleNovahGenerate}
                isStreaming={isStreaming}
                streamProgress={streamProgress}
                onExportCSV={handleBulkApplyAndDownload}
                onDownloadZIP={handleBulkApplyAndDownload}
                postsCount={posts.length + streamingPosts.length}
              />
            </CardContent>
          </Card>
          {/* Legacy settings hidden — Novah handles configuration */}
          <div style={{ display: 'none' }}>
            <CardContent>
              <InputWrapper style={{ marginBottom: 16 }}>
                <Label>Platforms & Post Counts</Label>
                <SocialNetworkGrid>
                  {SOCIAL_NETWORKS.map((network) => {
                    const isActive = platformCounts[network.id] !== undefined;
                    return (
                      <PlatformRow key={network.id} $active={isActive}>
                        <PlatformLabel>
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={() => togglePlatform(network.id)}
                          />
                          <PlatformName>{network.label}</PlatformName>
                        </PlatformLabel>
                        <CountInput
                          type="number"
                          min={0}
                          max={20}
                          value={platformCounts[network.id] || 0}
                          onChange={(e) => updatePlatformCount(network.id, Number(e.target.value))}
                          disabled={!isActive}
                        />
                      </PlatformRow>
                    );
                  })}
                </SocialNetworkGrid>
                <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#525252', fontWeight: 600 }}>
                  Total posts: {totalPostCount}
                </div>
              </InputWrapper>

              <ImageSourceSection>
                <Label>Image Sources</Label>
                <ImageSourceGrid>
                  <ImageSourceOption
                    type="button"
                    $active={enabledImageSources.includes('stock')}
                    onClick={() => toggleImageSource('stock')}
                  >
                    <ImageSourceIcon>📷</ImageSourceIcon>
                    <ImageSourceLabel>Stock Photos</ImageSourceLabel>
                    <ImageSourceDesc>From Pexels</ImageSourceDesc>
                  </ImageSourceOption>

                  <ImageSourceOption
                    type="button"
                    $active={enabledImageSources.includes('pastel')}
                    onClick={() => toggleImageSource('pastel')}
                  >
                    <ImageSourceIcon>🎨</ImageSourceIcon>
                    <ImageSourceLabel>Colored</ImageSourceLabel>
                    <ImageSourceDesc>Solid backgrounds</ImageSourceDesc>
                  </ImageSourceOption>

                  <ImageSourceOption
                    type="button"
                    $active={enabledImageSources.includes('asset')}
                    $disabled={backgroundImages.length === 0}
                    disabled={backgroundImages.length === 0}
                    onClick={() => toggleImageSource('asset')}
                  >
                    <ImageSourceIcon>🖼️</ImageSourceIcon>
                    <ImageSourceLabel>Your Images</ImageSourceLabel>
                    <ImageSourceDesc>{backgroundImages.length} available</ImageSourceDesc>
                  </ImageSourceOption>

                  <ImageSourceOption
                    type="button"
                    $active={enabledImageSources.includes('ai')}
                    onClick={() => toggleImageSource('ai')}
                  >
                    <ImageSourceIcon>🤖</ImageSourceIcon>
                    <ImageSourceLabel>AI Generated</ImageSourceLabel>
                    <ImageSourceDesc>Stable Diffusion</ImageSourceDesc>
                  </ImageSourceOption>
                </ImageSourceGrid>

                {/* Mix sliders when multiple sources selected */}
                {enabledImageSources.length > 1 && (
                  <ImageMixSlider>
                    <div style={{ fontSize: '0.75rem', color: '#525252', marginBottom: 8, fontWeight: 600 }}>
                      Distribution (adjust how many of each type)
                    </div>
                    {enabledImageSources.includes('stock') && (
                      <SliderRow>
                        <SliderLabel>Stock</SliderLabel>
                        <RangeSlider
                          type="range"
                          min={0}
                          max={100}
                          value={imageMix.stock}
                          onChange={(e) => updateImageMix('stock', Number(e.target.value))}
                        />
                        <SliderValue>{imageMix.stock}%</SliderValue>
                      </SliderRow>
                    )}
                    {enabledImageSources.includes('pastel') && (
                      <SliderRow>
                        <SliderLabel>Colored</SliderLabel>
                        <RangeSlider
                          type="range"
                          min={0}
                          max={100}
                          value={imageMix.pastel}
                          onChange={(e) => updateImageMix('pastel', Number(e.target.value))}
                        />
                        <SliderValue>{imageMix.pastel}%</SliderValue>
                      </SliderRow>
                    )}
                    {enabledImageSources.includes('asset') && (
                      <SliderRow>
                        <SliderLabel>Your Images</SliderLabel>
                        <RangeSlider
                          type="range"
                          min={0}
                          max={100}
                          value={imageMix.asset}
                          onChange={(e) => updateImageMix('asset', Number(e.target.value))}
                        />
                        <SliderValue>{imageMix.asset}%</SliderValue>
                      </SliderRow>
                    )}
                    {enabledImageSources.includes('ai') && (
                      <SliderRow>
                        <SliderLabel>AI Generated</SliderLabel>
                        <RangeSlider
                          type="range"
                          min={0}
                          max={100}
                          value={imageMix.ai}
                          onChange={(e) => updateImageMix('ai', Number(e.target.value))}
                        />
                        <SliderValue>{imageMix.ai}%</SliderValue>
                      </SliderRow>
                    )}
                  </ImageMixSlider>
                )}

                {/* Custom color picker when Colored source is enabled */}
                {enabledImageSources.includes('pastel') && (
                  <ColorPickerContainer>
                    <ColorPickerHeader>
                      <ColorPickerTitle>
                        Custom Colors {customColors.length > 0 && `(${customColors.length})`}
                      </ColorPickerTitle>
                      {customColors.length > 0 && (
                        <SmallButton onClick={clearAllColors}>Clear All</SmallButton>
                      )}
                    </ColorPickerHeader>
                    <ColorInputRow>
                      <ColorInput
                        type="color"
                        value={colorPickerValue}
                        onChange={(e) => setColorPickerValue(e.target.value)}
                      />
                      <AddColorButton onClick={addCustomColor}>Add Color</AddColorButton>
                    </ColorInputRow>
                    <SelectedColorsContainer>
                      {customColors.length === 0 ? (
                        <NoColorsMessage>
                          No colors selected. Random colors will be used.
                        </NoColorsMessage>
                      ) : (
                        customColors.map((color, index) => (
                          <ColorChip key={`${color}-${index}`} $color={color}>
                            <ColorNumber>{index + 1}</ColorNumber>
                            <ColorSwatch $color={color} />
                            <span>{color.toUpperCase()}</span>
                            <RemoveColorButton onClick={() => removeCustomColor(index)}>
                              ×
                            </RemoveColorButton>
                          </ColorChip>
                        ))
                      )}
                    </SelectedColorsContainer>
                  </ColorPickerContainer>
                )}

                {/* Asset selector when asset source is enabled */}
                {enabledImageSources.includes('asset') && backgroundImages.length > 0 && (
                  <AssetSelectorContainer>
                    <AssetSelectorHeader>
                      <AssetSelectorTitle>
                        Select Images ({selectedAssetIds.length}/{backgroundImages.length})
                      </AssetSelectorTitle>
                      <AssetSelectorButtons>
                        <SmallButton onClick={selectAllAssets}>Select All</SmallButton>
                        <SmallButton onClick={deselectAllAssets}>Clear</SmallButton>
                      </AssetSelectorButtons>
                    </AssetSelectorHeader>
                    <AssetSelector>
                      {backgroundImages.map((asset) => (
                        <AssetItem
                          key={asset.id}
                          $selected={selectedAssetIds.includes(asset.id)}
                          onClick={() => toggleAssetSelection(asset.id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedAssetIds.includes(asset.id)}
                            readOnly
                          />
                          <AssetThumb src={asset.url} alt={asset.name} />
                          <AssetName>{asset.name}</AssetName>
                        </AssetItem>
                      ))}
                    </AssetSelector>
                  </AssetSelectorContainer>
                )}

                {/* Message when Your Images is enabled but no images uploaded */}
                {enabledImageSources.includes('asset') && backgroundImages.length === 0 && (
                  <AssetSelectorContainer>
                    <AssetSelectorHeader>
                      <AssetSelectorTitle style={{ color: '#EF4444' }}>
                        No images uploaded yet
                      </AssetSelectorTitle>
                    </AssetSelectorHeader>
                    <div style={{ padding: '12px', fontSize: '0.85rem', color: '#525252' }}>
                      Go to your Workspace page and upload images in the "Background Images" section to use them here.
                    </div>
                  </AssetSelectorContainer>
                )}
              </ImageSourceSection>

              <InputWrapper style={{ marginBottom: 16 }}>
                <Label htmlFor="customPrompt">Custom Instructions (optional)</Label>
                <TextArea
                  id="customPrompt"
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="e.g., Focus on summer promotions, use casual tone..."
                />
              </InputWrapper>

              {(error || templateError) && <ErrorMessage style={{ marginBottom: 16 }}>{error || templateError}</ErrorMessage>}
              <Button
                $fullWidth
                onClick={handleGenerateStream}
                disabled={isGenerating || isStreaming || totalPostCount === 0 || (user?.credits ?? 0) < totalPostCount}
              >
                {isGenerating || isStreaming
                  ? 'Generating...'
                  : (user?.credits ?? 0) < totalPostCount
                    ? `Not enough credits (need ${totalPostCount}, have ${user?.credits ?? 0})`
                    : `Generate ${totalPostCount} Posts (${totalPostCount} credits)`}
              </Button>
            </CardContent>
          </div>
        </div>

        <div>
          {isGenerating ? (
            <GeneratingOverlay>
              <Spinner />
              <GeneratingText>
                Generating {totalPostCount} posts...
                <br />
                Estimated time: ~{Math.max(10, totalPostCount * 5)} seconds
              </GeneratingText>
            </GeneratingOverlay>
          ) : isStreaming || streamingPosts.length > 0 ? (
            <>
              {isStreaming && streamProgress.total > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <StreamProgressText>
                    <span>Generating posts... ~{Math.max(5, (streamProgress.total - streamProgress.current) * 5)}s remaining</span>
                    <span>{streamProgress.current}/{streamProgress.total}</span>
                  </StreamProgressText>
                  <StreamProgressBarContainer>
                    <StreamProgressBarFill $progress={(streamProgress.current / streamProgress.total) * 100} />
                  </StreamProgressBarContainer>
                </div>
              )}
              <PostsGrid>
                {/* Show streaming posts as they arrive */}
                {streamingPosts.map((post) => {
                  const effectiveFormat = postFormatOverrides[post.id] || previewFormat;
                  return (
                  <PostCard key={post.id} onClick={() => setSelectedPost(post)}>
                    <PostImage $aspectRatio={PREVIEW_FORMATS[effectiveFormat].aspectRatio}>
                      {post.pendingImageTaskId && !post.imageUrl ? (
                        <AiLoadingOverlay>
                          <AiLoadingSpinner />
                          <AiLoadingText>Generating AI image...</AiLoadingText>
                        </AiLoadingOverlay>
                      ) : post.imageUrl ? (
                        compositeThumbnails[post.id] ? (
                          <img src={compositeThumbnails[post.id]} alt="Post" />
                        ) : isPastelUri(post.imageUrl) ? (
                          <PastelBackground $color={parsePastelUri(post.imageUrl) || '#F0F0F0'} />
                        ) : (
                          <img src={post.imageUrl} alt="Post" />
                        )
                      ) : null}
                    </PostImage>
                    <HoverOverlay>
                      <div style={{ fontSize: 13, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {post.content}
                      </div>
                    </HoverOverlay>
                    <PostCardFooter>
                      <PostNetwork>{NETWORK_ICONS[post.network] || '📱'} {post.network}</PostNetwork>
                      {post.contentCategory && <CategoryBadge $category={post.contentCategory}>{CONTENT_CATEGORIES[post.contentCategory]?.label || post.contentCategory}</CategoryBadge>}
                      {post.imageSource && <ImageSourceBadge $source={post.imageSource}>{getImageSourceLabel(post.imageSource, post.aiModel, post.aiModelDetail)}</ImageSourceBadge>}
                    </PostCardFooter>
                  </PostCard>
                );})}

                {/* Show remaining skeleton placeholders */}
                {Array.from({ length: skeletonCount }).map((_, i) => (
                  <SkeletonCard key={`skeleton-${i}`}>
                    <SkeletonImage $aspectRatio={PREVIEW_FORMATS[previewFormat].aspectRatio} />
                    <SkeletonContent>
                      <SkeletonLine $width="80%" />
                      <SkeletonLine $width="100%" />
                      <SkeletonLine $width="60%" />
                    </SkeletonContent>
                  </SkeletonCard>
                ))}
              </PostsGrid>
            </>
          ) : posts.length === 0 ? (
            <EmptyState>
              <p>No posts generated yet. Configure your settings and click Generate!</p>
            </EmptyState>
          ) : (
            <>
            {selectedPostIds.size > 0 && (
              <SelectionBar>
                <span>{selectedPostIds.size} selected</span>
                <SelectionTemplateSelect
                  onChange={async (e) => {
                    const templateId = e.target.value;
                    if (!templateId) return;
                    // Apply template and re-render thumbnails for all selected posts
                    for (const postId of selectedPostIds) {
                      try {
                        await handlePostTemplateChange(postId, templateId);
                      } catch { /* ignore */ }
                    }
                    e.target.value = '';
                  }}
                  defaultValue=""
                >
                  <option value="">Apply Template...</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </SelectionTemplateSelect>
                <SelectionBarBtn
                  onClick={async () => {
                    if (templates.length === 0) return;
                    // Fisher-Yates shuffle so distribution is even — every template
                    // gets used roughly the same number of times instead of some
                    // appearing many times and others not at all.
                    const shuffled = [...templates];
                    for (let i = shuffled.length - 1; i > 0; i--) {
                      const j = Math.floor(Math.random() * (i + 1));
                      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                    }
                    const ids = Array.from(selectedPostIds);
                    // Shuffle the post IDs too so the assignment isn't always
                    // in the same order — keeps consecutive posts diverse.
                    for (let i = ids.length - 1; i > 0; i--) {
                      const j = Math.floor(Math.random() * (i + 1));
                      [ids[i], ids[j]] = [ids[j], ids[i]];
                    }
                    for (let i = 0; i < ids.length; i++) {
                      const tpl = shuffled[i % shuffled.length];
                      try {
                        await handlePostTemplateChange(ids[i], tpl.id);
                      } catch { /* ignore */ }
                    }
                  }}
                  title={`Randomly distribute the ${templates.length} available templates across the ${selectedPostIds.size} selected posts`}
                >
                  🎲 Randomize ({templates.length})
                </SelectionBarBtn>
                <SelectionBarBtn onClick={() => {
                  const all = new Set(posts.map(p => p.id));
                  setSelectedPostIds(all);
                }}>Select All</SelectionBarBtn>
                <SelectionBarBtn onClick={() => setSelectedPostIds(new Set())}>Clear</SelectionBarBtn>
                <SelectionBarBtn
                  onClick={async () => {
                    const count = selectedPostIds.size;
                    if (count === 0) return;
                    const confirmed = window.confirm(
                      `Delete ${count} post${count === 1 ? '' : 's'}? This cannot be undone.`
                    );
                    if (!confirmed) return;
                    const ids = Array.from(selectedPostIds);
                    for (const postId of ids) {
                      try {
                        await handleDeletePost(postId);
                      } catch { /* ignore individual failures, keep deleting the rest */ }
                    }
                    setSelectedPostIds(new Set());
                  }}
                  style={{ background: '#dc2626', color: 'white', borderColor: '#dc2626' }}
                  title={`Delete ${selectedPostIds.size} selected post${selectedPostIds.size === 1 ? '' : 's'}`}
                >
                  🗑 Delete ({selectedPostIds.size})
                </SelectionBarBtn>
              </SelectionBar>
            )}
            <PostsGrid>
              {posts.map((post) => {
                const effectiveFormat = postFormatOverrides[post.id] || previewFormat;
                const isSelected = selectedPostIds.has(post.id);
                return (
                <PostCard key={post.id} onClick={() => setSelectedPost(post)}>
                  <SelectCheckbox
                    $selected={isSelected}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPostIds(prev => {
                        const next = new Set(prev);
                        if (next.has(post.id)) next.delete(post.id);
                        else next.add(post.id);
                        return next;
                      });
                    }}
                  >
                    {isSelected ? '✓' : ''}
                  </SelectCheckbox>
                  <PostImage $aspectRatio={PREVIEW_FORMATS[effectiveFormat].aspectRatio}>
                    {post.pendingImageTaskId && !post.imageUrl ? (
                      <AiLoadingOverlay>
                        <AiLoadingSpinner />
                        <AiLoadingText>Generating AI image...</AiLoadingText>
                      </AiLoadingOverlay>
                    ) : post.imageUrl ? (
                      compositeThumbnails[post.id] ? (
                        <img src={compositeThumbnails[post.id]} alt="Post" />
                      ) : isPastelUri(post.imageUrl) ? (
                        <PastelBackground $color={parsePastelUri(post.imageUrl) || '#F0F0F0'} />
                      ) : (
                        <img src={post.imageUrl} alt="Post" />
                      )
                    ) : null}
                  </PostImage>
                  <HoverOverlay>
                    <div style={{ fontSize: 13, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {post.content}
                    </div>
                  </HoverOverlay>
                  <PostCardFooter>
                    <PostNetwork>{NETWORK_ICONS[post.network] || '📱'} {post.network}</PostNetwork>
                    {post.contentCategory && <CategoryBadge $category={post.contentCategory}>{CONTENT_CATEGORIES[post.contentCategory]?.label || post.contentCategory}</CategoryBadge>}
                    {post.imageSource && <ImageSourceBadge $source={post.imageSource}>{getImageSourceLabel(post.imageSource, post.aiModel, post.aiModelDetail)}</ImageSourceBadge>}
                  </PostCardFooter>
                </PostCard>
              );})}
            </PostsGrid>
            </>
          )}

          {/* Bulk actions bar — at bottom after posts */}
          {posts.length > 0 && (
            <BulkActionsBar style={{ marginTop: 16 }}>
              <BulkLabel>Preview Format:</BulkLabel>
              <FormatSelector
                value={previewFormat}
                onChange={setPreviewFormat}
                disabled={isBulkProcessing}
              />
              {templates.length > 0 && (
                <>
                  <BulkLabel>Apply Template to All:</BulkLabel>
                  <TemplateSelect
                    value={bulkTemplateId}
                    onChange={(e) => setBulkTemplateId(e.target.value)}
                    disabled={isBulkProcessing}
                  >
                    <option value="">Select Template</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.canvasWidth}x{template.canvasHeight})
                      </option>
                    ))}
                  </TemplateSelect>

                  {isBulkProcessing ? (
                    <>
                      <ProgressBar>
                        <ProgressFill $progress={(bulkProgress.current / bulkProgress.total) * 100} />
                      </ProgressBar>
                      <ProgressText>
                        {bulkProgress.current}/{bulkProgress.total}
                      </ProgressText>
                    </>
                  ) : (
                    <Button
                      onClick={() => handleBulkApplyAndDownload('mobile')}
                      disabled={!bulkTemplateId || postsWithImages.length === 0}
                    >
                      Download All as ZIP
                    </Button>
                  )}
                </>
              )}
              <div style={{ marginLeft: 'auto' }}>
                <Button
                  $size="sm"
                  $variant="outline"
                  style={{ color: '#EF4444', borderColor: '#EF4444' }}
                  onClick={async () => {
                    if (!confirm(`Delete all ${posts.length} posts? This cannot be undone.`)) return;
                    try {
                      await apiClient.delete(`/api/workspaces/${workspaceId}/posts`);
                      setPosts([]);
                      setStreamingPosts([]);
                      setCompositeThumbnails({});
                    } catch (err) {
                      console.error('Failed to delete all posts:', err);
                    }
                  }}
                >
                  Delete All
                </Button>
              </div>
            </BulkActionsBar>
          )}
        </div>
      </Grid>

      {selectedPost && (
        <PreviewModal
          isOpen={!!selectedPost}
          onClose={() => setSelectedPost(null)}
          post={selectedPost}
          posts={posts}
          templates={templates}
          workspaceId={workspaceId || ''}
          workspaceName={workspace?.name || ''}
          initialTemplateId={postTemplateOverrides[selectedPost.id] || (bulkTemplate ? getTemplateForPost(selectedPost, bulkTemplate).id : bulkTemplateId)}
          previewFormat={previewFormat}
          onPreviewFormatChange={setPreviewFormat}
          onSaveOffset={handlePreviewSaveOffset}
          onTemplateChange={handlePostTemplateChange}
          onTemplateCreated={handleTemplateCreated}
          workspaceLogoUrl={workspaceLogoUrl}
          logoScale={workspace?.logoScale}
          reviewCapability={reviewCapability}
          onContentSaved={(postId, content) => {
            setPosts(prev => prev.map(p => p.id === postId ? { ...p, content } : p));
            setSelectedPost(prev => prev && prev.id === postId ? { ...prev, content } : prev);
          }}
          onNavigateBilling={() => navigate('/billing')}
          brandColors={templateState.brandColors}
          brandFonts={templateState.brandFonts}
        />
      )}

      {/* Regenerate Modal */}
      {regenPostId && (
        <RegenModal onClick={() => !isRegenerating && setRegenPostId(null)}>
          <RegenModalContent onClick={(e) => e.stopPropagation()}>
            <RegenModalTitle>Regenerate Post</RegenModalTitle>
            <RegenOptions>
              <RegenOption
                onClick={() => handleRegenerate('image')}
                disabled={!!isRegenerating}
                $loading={isRegenerating === 'image'}
              >
                {isRegenerating === 'image' ? 'Regenerating...' : 'Image Only'}
              </RegenOption>
              <RegenOption
                onClick={() => handleRegenerate('text')}
                disabled={!!isRegenerating}
                $loading={isRegenerating === 'text'}
              >
                {isRegenerating === 'text' ? 'Regenerating...' : 'Text Only'}
              </RegenOption>
              <RegenOption
                onClick={() => handleRegenerate('all')}
                disabled={!!isRegenerating}
                $loading={isRegenerating === 'all'}
              >
                {isRegenerating === 'all' ? 'Regenerating...' : 'Both (Image + Text)'}
              </RegenOption>
            </RegenOptions>
            {!isRegenerating && (
              <RegenCancelButton onClick={() => setRegenPostId(null)}>
                Cancel
              </RegenCancelButton>
            )}
          </RegenModalContent>
        </RegenModal>
      )}

      {showAiFallbackToast && (
        <Toast
          title="AI GENERATION LIMIT"
          message="Some images couldn't be generated with AI. Using stock photos instead."
          variant="warning"
          duration={5000}
          onClose={() => setShowAiFallbackToast(false)}
        />
      )}
    </>
  );
}
